import { useCallback, useSyncExternalStore } from 'react';
import type { ObjectData, SceneStore } from '@os/doc';

/**
 * Drawn in world coordinates from the scene graph (its element sits at the world origin): ends follow their targets
 * whenever the graph changes, i.e. on every document write of a target (15 Hz during a drag).
 */
export function ConnectorObject({ o, store }: { o: Extract<ObjectData, { type: 'connector' }>; store: SceneStore }) {
  const subscribe = useCallback((cb: () => void) => store.subscribeList(cb), [store]);
  const drawing = useSyncExternalStore(subscribe, () => store.graph().paths.get(o.id));
  if (!drawing) return null;
  const stroke = typeof o.style.stroke === 'string' ? o.style.stroke : 'var(--canvas-ink, #141414)';
  const marker = `cap-${o.id}`;
  const cap = o.startCap === 'arrow' || o.endCap === 'arrow';
  return (
    <>
    <svg width={1} height={1} className="pointer-events-none absolute left-0 top-0 block overflow-visible" aria-hidden="true">
      {cap && (
        <defs>
          <marker id={marker} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" style={{ fill: stroke }} />
          </marker>
        </defs>
      )}
      <path
        data-line
        d={drawing.d}
        markerStart={o.startCap === 'arrow' ? `url(#${marker})` : undefined}
        markerEnd={o.endCap === 'arrow' ? `url(#${marker})` : undefined}
        style={{ fill: 'none', stroke, strokeWidth: 2, strokeLinejoin: 'round' }}
      />
    </svg>
    {o.label && (
      // The text on the line, centred on its midpoint at a constant screen size.
      <div
        data-connector-label={o.id}
        className="pointer-events-none absolute left-0 top-0 max-w-[240px] origin-top-left truncate whitespace-nowrap rounded-md bg-white px-1.5 py-0.5 font-sans text-xs text-[#141414] shadow-sm ring-1 ring-black/10"
        style={{ transform: `translate(${drawing.mid.x}px, ${drawing.mid.y}px) scale(var(--inv-zoom, 1)) translate(-50%, -50%)` }}
      >
        {o.label}
      </div>
    )}
    </>
  );
}
